import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { render, scene } from '@/threejs/core';

/**
 * LightingManager - Centralized management for scene lighting and brightness
 * Handles ambient light, directional light, lighting presets, and brightness settings
 */
class LightingManager {
  constructor() {
    this.ambientLightEnabled = false;
    this.ambientLightIntensity = 0.2;
    this.directionalLightEnabled = false;
    this.directionalLightIntensity = 0.5;
    this.directionalLightYaw = 0;
    this.directionalLightPitch = 45;
    this.currentPreset = 'pitch_black';
    this.isInitialized = false;

    // Lighting presets configuration
    this.presets = {
      pitch_black: {
        ambient: 0,
        directional: 0,
        ambientEnabled: false,
        directionalEnabled: false,
      },
      moonlight: {
        ambient: 0.05,
        directional: 0.1,
        ambientEnabled: true,
        directionalEnabled: true,
      },
      low_room: {
        ambient: 0.1,
        directional: 0,
        ambientEnabled: true,
        directionalEnabled: false,
      },
      normal_room: {
        ambient: 0.3,
        directional: 0,
        ambientEnabled: true,
        directionalEnabled: false,
      },
      bright_room: {
        ambient: 0.8,
        directional: 0,
        ambientEnabled: true,
        directionalEnabled: false,
      },
      overcast: {
        ambient: 0.2,
        directional: 0.8,
        ambientEnabled: true,
        directionalEnabled: true,
      },
      sunny: {
        ambient: 0.5,
        directional: 2.0,
        ambientEnabled: true,
        directionalEnabled: true,
      },
    };
  }

  /**
   * Initialize the lighting manager
   */
  initialize() {
    this.isInitialized = true;
    return this;
  }

  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Set ambient light enabled state
   * @param {boolean} enabled - Whether ambient light is enabled
   */
  setAmbientLightEnabled(enabled) {
    this.ambientLightEnabled = enabled;
    this.updateAmbientLight();
  }

  /**
   * Set ambient light intensity
   * @param {number} intensity - Ambient light intensity
   */
  setAmbientLightIntensity(intensity) {
    this.ambientLightIntensity = intensity;
    this.updateAmbientLight();
  }

  /**
   * Update ambient light in scene
   */
  updateAmbientLight() {
    scene.traverse((child) => {
      if (child.type === 'AmbientLight') {
        child.intensity = this.ambientLightIntensity;
        child.visible = this.ambientLightEnabled;
        child.needsUpdate = true;
      }
    });
    render();
  }

  /**
   * Set directional light enabled state
   * @param {boolean} enabled - Whether directional light is enabled
   */
  setDirectionalLightEnabled(enabled) {
    this.directionalLightEnabled = enabled;
    this.updateDirectionalLight();
  }

  /**
   * Set directional light intensity
   * @param {number} intensity - Directional light intensity
   */
  setDirectionalLightIntensity(intensity) {
    this.directionalLightIntensity = intensity;
    this.updateDirectionalLight();
  }

  /**
   * Set directional light yaw (horizontal rotation)
   * @param {number} yaw - Yaw angle in degrees
   */
  setDirectionalLightYaw(yaw) {
    this.directionalLightYaw = yaw;
    this.updateDirectionalLight();
  }

  /**
   * Set directional light pitch (vertical rotation)
   * @param {number} pitch - Pitch angle in degrees
   */
  setDirectionalLightPitch(pitch) {
    this.directionalLightPitch = pitch;
    this.updateDirectionalLight();
  }

  /**
   * Update directional light in scene
   */
  updateDirectionalLight() {
    // Convert degrees to radians
    const yawRad = (this.directionalLightYaw * Math.PI) / 180;
    const pitchRad = (this.directionalLightPitch * Math.PI) / 180;

    // Calculate light position based on yaw and pitch
    // Using spherical coordinates: radius = 10 (arbitrary distance)
    const radius = 10;
    const x = radius * Math.cos(pitchRad) * Math.sin(yawRad);
    const y = radius * Math.sin(pitchRad);
    const z = radius * Math.cos(pitchRad) * Math.cos(yawRad);

    scene.traverse((child) => {
      if (child.type === 'DirectionalLight') {
        child.intensity = this.directionalLightIntensity;
        child.visible = this.directionalLightEnabled;
        child.position.set(x, y, z);
        child.needsUpdate = true;
      }
    });
    render();
  }

  /**
   * Apply a lighting preset
   * @param {string} presetName - Name of the preset to apply
   */
  setLightingPreset(presetName) {
    const preset = this.presets[presetName];
    if (!preset) {
      console.warn(`Unknown lighting preset: ${presetName}`);
      return;
    }

    this.currentPreset = presetName;
    this.ambientLightEnabled = preset.ambientEnabled;
    this.ambientLightIntensity = preset.ambient;
    this.directionalLightEnabled = preset.directionalEnabled;
    this.directionalLightIntensity = preset.directional;

    // Update scene lights
    this.updateAmbientLight();
    this.updateDirectionalLight();

    // Update store to reflect the changes
    this.updateStoreSettings(preset);
  }

  /**
   * Update the Crux module store with lighting settings
   * @param {object} settings - Lighting settings to update in store
   */
  updateStoreSettings(settings) {
    try {
      const cruxModuleStore = useCruxModuleStore();

      cruxModuleStore.mergeSettingModule(null, 'viewport_settings', 'brightness.ambientLight', settings.ambientEnabled);
      cruxModuleStore.mergeSettingModule(
        null,
        'viewport_settings',
        'brightness.ambientLightIntensity',
        settings.ambient
      );
      cruxModuleStore.mergeSettingModule(
        null,
        'viewport_settings',
        'brightness.directionalLight',
        settings.directionalEnabled
      );
      cruxModuleStore.mergeSettingModule(
        null,
        'viewport_settings',
        'brightness.directionalLightIntensity',
        settings.directional
      );
    } catch (error) {
      console.warn('Could not update store settings:', error);
    }
  }

  /**
   * Get all available preset names
   * @returns {string[]} Array of preset names
   */
  getAvailablePresets() {
    return Object.keys(this.presets);
  }

  /**
   * Get preset configuration
   * @param {string} presetName - Name of the preset
   * @returns {object|null} Preset configuration or null if not found
   */
  getPresetConfig(presetName) {
    return this.presets[presetName] || null;
  }

  /**
   * Add or update a lighting preset
   * @param {string} name - Preset name
   * @param {object} config - Preset configuration
   */
  addPreset(name, config) {
    this.presets[name] = { ...config };
  }

  /**
   * Handle lighting setting changes
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleLightingSetting(key, value) {
    switch (key) {
      case 'brightness.preset':
        //this.setLightingPreset(value)
        break;
      case 'brightness.directionalLight':
        this.setDirectionalLightEnabled(value);
        break;
      case 'brightness.directionalLightIntensity':
        this.setDirectionalLightIntensity(value);
        break;
      case 'brightness.directionalLightYaw':
        this.setDirectionalLightYaw(value);
        break;
      case 'brightness.directionalLightPitch':
        this.setDirectionalLightPitch(value);
        break;
      case 'brightness.ambientLight':
        this.setAmbientLightEnabled(value);
        break;
      case 'brightness.ambientLightIntensity':
        this.setAmbientLightIntensity(value);
        break;
      default:
        console.warn(`Unknown lighting setting: ${key}`);
    }
  }

  /**
   * Get current lighting configuration
   * @returns {object} Current lighting settings
   */
  getLightingConfig() {
    return {
      ambientLightEnabled: this.ambientLightEnabled,
      ambientLightIntensity: this.ambientLightIntensity,
      directionalLightEnabled: this.directionalLightEnabled,
      directionalLightIntensity: this.directionalLightIntensity,
      directionalLightYaw: this.directionalLightYaw,
      directionalLightPitch: this.directionalLightPitch,
      currentPreset: this.currentPreset,
      availablePresets: this.getAvailablePresets(),
    };
  }

  /**
   * Dispose of lighting resources
   */
  dispose() {
    this.isInitialized = false;
  }
}

// Create and export singleton instance
export const lightingManager = new LightingManager();

// Export the class for testing or multiple instances if needed
export { LightingManager };
